import { AlertTriangle, RotateCw, WifiOff } from "lucide-react";
import type { ReactNode } from "react";
import { OfflineError, errorMessage } from "../lib/api";
import { Button } from "./Button";

export function EmptyState({ icon, title, children, actions, tone = "plain" }: { icon?: ReactNode; title: string; children?: ReactNode; actions?: ReactNode; tone?: "plain" | "search" }) {
  return (
    <section className={`empty-state empty-state--${tone}`} aria-label={title}>
      {icon ? <div className="empty-state__icon" aria-hidden="true">{icon}</div> : null}
      <h2 className="empty-state__title">{title}</h2>
      {children ? <div className="empty-state__body">{children}</div> : null}
      {actions ? <div className="empty-state__actions">{actions}</div> : null}
    </section>
  );
}

export function ErrorState({ error, title, onRetry, retrying }: { error: unknown; title?: string; onRetry?: () => void; retrying?: boolean }) {
  const offline = error instanceof OfflineError;
  return (
    <section className="error-state" role="alert">
      <div className="error-state__icon" aria-hidden="true">
        {offline ? <WifiOff size={28} /> : <AlertTriangle size={28} />}
      </div>
      <h2 className="error-state__title">{title ?? (offline ? "Not available offline" : "Couldn't load this")}</h2>
      <p className="error-state__body">{errorMessage(error)}</p>
      {onRetry ? (
        <Button emphasis="outline" intent="neutral" icon={<RotateCw size={18} />} busy={retrying} onClick={onRetry}>
          Try again
        </Button>
      ) : null}
    </section>
  );
}

export function Badge({ tone = "neutral", icon, children, className }: { tone?: "neutral" | "info" | "success" | "warning" | "danger" | "brand"; icon?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <span className={["badge", `badge--${tone}`, className ?? ""].join(" ")}>
      {icon ? <span className="badge__icon" aria-hidden="true">{icon}</span> : null}
      {children}
    </span>
  );
}
